import React, { useEffect, useState } from 'react'
import {
    Autocomplete,
    Grid,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    InputAdornment,
    IconButton,
    Tooltip,
    CircularProgress,
    Box,
} from '@mui/material';
import SectionTitle from '../../../ui/sectionTitle';
import { Add, Search } from '@mui/icons-material';
import { SelectLocation } from '../../../common';
import { ValidDataService } from '../../../../service/validDataService';
import { toast } from 'react-toastify';

function ValidFrameBasicInfo({
    formik,
    setSupplierDialogOpen,
    setSupplierCreateDialogOpen,
    setBrandCreateDialogOpen,
    labels,
    disableModelNumber = false
}) {
    const [brands, setBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    
    const updateLocation = (location) => {
        formik.setFieldValue('location', location)
    }

    useEffect(() => {
        if (!formik.values.supplierId) {
            setBrands([]);
            return;
        }

        const loadBrands = async () => {
            setLoadingBrands(true);
            try {
                const response = await ValidDataService.getBrandsForSupplier(formik.values.supplierId);
                if (response.code === 0 && Array.isArray(response.data)) {
                    setBrands(response.data);
                } else {
                    setBrands([]);
                }
            } catch (error) {
                console.error('Error loading brands:', error);
                toast.error('Failed to load brands');
                setBrands([]);
            } finally {
                setLoadingBrands(false);
            }
        };

        loadBrands();
    }, [formik.values.supplierId]);

    const handleBrandChange = (event, value) => {
        if (typeof value === 'string') {
            formik.setFieldValue('brand', value);
            formik.setFieldValue('brandId', '');
            return;
        }

        if (value && value.inputValue) {
            formik.setFieldValue('brand', value.inputValue);
            formik.setFieldValue('brandId', '');
            return;
        }

        if (value) {
            formik.setFieldValue('brand', value.name);
            formik.setFieldValue('brandId', value.id);
            return;
        }

        formik.setFieldValue('brand', '');
        formik.setFieldValue('brandId', '');
    };

    return (
        <>
            <Grid item xs={12}>
                <SectionTitle title="Basic Information" />
            </Grid>
            <Grid item xs={12} sm={4} mt={1}>
                <TextField
                    id="supplierName"
                    name="supplierName"
                    label="Supplier"
                    size='small'
                    fullWidth
                    value={formik.values.supplierName}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.supplierName)}
                    helperText={formik.errors.supplierName}
                    inputProps={{
                        style: {
                            textTransform: 'capitalize'
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Tooltip title="Select supplier">
                                        <IconButton
                                            onClick={() => setSupplierDialogOpen(true)}
                                            size="small"
                                        >
                                            <Search />
                                        </IconButton>
                                    </Tooltip>
                                    {setSupplierCreateDialogOpen && (
                                        <Tooltip title="Add supplier">
                                            <IconButton
                                                onClick={() => setSupplierCreateDialogOpen()}
                                                size="small"
                                            >
                                                <Add />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={4} mt={1}>
                <Autocomplete
                    id="brand"
                    freeSolo
                    options={brands}
                    disabled={!formik.values.supplierId}
                    loading={loadingBrands}
                    value={brands.find((brand) => brand.id === formik.values.brandId) || formik.values.brand || ''}
                    onChange={handleBrandChange}
                    getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                            return option;
                        }
                        if (option.inputValue) {
                            return option.inputValue;
                        }
                        return option.name || '';
                    }}
                    filterOptions={(options, params) => {
                        const filtered = options.filter((option) =>
                            option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                        );

                        const { inputValue } = params;
                        const isExisting = options.some((option) => option.name.toLowerCase() === inputValue.toLowerCase());
                        if (inputValue !== '' && !isExisting) {
                            filtered.push({
                                inputValue,
                                name: `Use "${inputValue}"`,
                            });
                        }

                        return filtered;
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            name="brand"
                            label="Brand"
                            size='small'
                            error={Boolean(formik.errors.brand)}
                            helperText={formik.errors.brand || (!formik.values.supplierId ? 'Select supplier first to load brands' : 'Choose an existing brand or type a new one')}
                            inputProps={{
                                ...params.inputProps,
                                style: {
                                    textTransform: 'capitalize'
                                }
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingBrands ? <CircularProgress color="inherit" size={20} /> : null}
                                        {setBrandCreateDialogOpen ? (
                                            <Tooltip title={formik.values.supplierId ? 'Add brand for this supplier' : 'Select supplier first'}>
                                                <span>
                                                    <IconButton
                                                        onClick={() => setBrandCreateDialogOpen()}
                                                        size="small"
                                                        disabled={!formik.values.supplierId}
                                                    >
                                                        <Add />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={4} mt={1}>
                <TextField
                    id={"modelNumber"}
                    name="modelNumber"
                    label={labels.modelNumber ||"Model Number"}
                    size='small'
                    fullWidth
                    disabled={disableModelNumber}
                    value={formik.values.modelNumber}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.modelNumber)}
                    helperText={formik.errors.modelNumber}
                />
            </Grid>
            <Grid item xs={12} sm={3} mt={1}>
                <TextField
                    id="sku"
                    name="sku"
                    label="SKU"
                    size='small'
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.sku}
                    onChange={formik.handleChange}
                    error={formik.touched.sku && Boolean(formik.errors.sku)}
                    helperText={formik.touched.sku && formik.errors.sku}
                />
            </Grid>            
            <Grid item xs={12} sm={2} mt={1}>
                <TextField
                    id="internalUpc"
                    name="internalUpc"
                    label="Internal UPC"
                    size='small'
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.internalUpc}
                    onChange={formik.handleChange}
                    error={formik.touched.internalUpc && Boolean(formik.errors.internalUpc)}
                    helperText={formik.touched.internalUpc && formik.errors.internalUpc}
                />
            </Grid>
            <Grid item xs={12} sm={2} mt={1}>
                <TextField
                    id="supplierUpc"
                    name="supplierUpc"
                    label="Supplier UPC"
                    size='small'
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.supplierUpc}
                    onChange={formik.handleChange}
                    error={formik.touched.supplierUpc && Boolean(formik.errors.supplierUpc)}
                    helperText={formik.touched.supplierUpc && formik.errors.supplierUpc}
                />
                {/* <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    id="type"
                                    name="type"
                                    label="Type"
                                    size='small'
                                    fullWidth
                                    value={formik.values.type}
                                    onChange={formik.handleChange}
                                    error={formik.touched.type && Boolean(formik.errors.type)}
                                >
                                    {frameTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl> */}
            </Grid>
            <Grid item xs={12} sm={2} mt={1} mr={2}>
                {
                    <SelectLocation fullWidth={true} location={formik.values.location.id || ''} error={Boolean(formik.errors.location)} required={true}
                    helperText={formik.errors.location} setLocation={updateLocation}
                 />
                }
                
            </Grid>
            <Grid item xs={12} sm={2} mt={1}>
                <FormControl fullWidth>
                    <InputLabel id='status-label'>Status</InputLabel>
                    <Select
                        id="status"
                        name="status"
                        label="Status"
                        size='small'
                        value={formik.values.status}
                        onChange={formik.handleChange}
                        error={formik.touched.status && Boolean(formik.errors.status)}
                    >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
        </>
    )
}

export default ValidFrameBasicInfo